/**
 * Pure helpers for the Service Desk queue grid: the column definitions, how a
 * sort click maps onto the api's `sort` parameter, how filters round-trip
 * through the address bar, and the client-side comparators.
 *
 * The api sorts three ways (latest activity, created, deadline). Latest
 * activity and created run in one direction each; a click on one of those
 * columns in its natural direction asks the api, so the page of tickets that
 * loads is the right page. The deadline sort runs in both directions in the
 * api, because the live deadline depends on business hours and pauses and
 * cannot be worked out in the browser from the rows on screen. Every other
 * click, and the reverse of created and latest activity, orders the tickets
 * already loaded on screen.
 */

import type { SdPriority, SdStatus } from '@ampersandbrands/ui';
import type { SdQueueFilters, SdStaffTicketRow } from '@/lib/serviceDeskTypes';
import { ALL_PRIORITIES, ALL_STATUSES, OPEN_STATUSES } from './sdLabels';

export type SortDirection = 'asc' | 'desc';

export type QueueColumn =
  | 'key'
  | 'title'
  | 'reporter'
  | 'store'
  | 'category'
  | 'priority'
  | 'status'
  | 'assignee'
  | 'deadline'
  | 'activity';

export type QueueSort = { column: QueueColumn; dir: SortDirection };

type ServerSort = SdQueueFilters['sort'];

type ColumnDef = {
  label: string;
  /** The direction the first click sorts in. */
  first: SortDirection;
  /** The api ordering that matches `first`, when there is one. */
  server?: ServerSort;
};

export const QUEUE_COLUMNS: Record<QueueColumn, ColumnDef> = {
  key: { label: 'Key', first: 'desc', server: 'created' },
  title: { label: 'Title', first: 'asc' },
  reporter: { label: 'Reporter', first: 'asc' },
  store: { label: 'Store', first: 'asc' },
  category: { label: 'Category', first: 'asc' },
  priority: { label: 'Priority', first: 'desc' },
  status: { label: 'Status', first: 'asc' },
  assignee: { label: 'Assigned to', first: 'asc' },
  deadline: { label: 'Deadline', first: 'asc', server: 'due' },
  activity: { label: 'Last activity', first: 'desc', server: 'last_activity' },
};

export const COLUMN_ORDER: QueueColumn[] = [
  'key',
  'title',
  'reporter',
  'store',
  'category',
  'priority',
  'status',
  'assignee',
  'deadline',
  'activity',
];

const opposite = (d: SortDirection): SortDirection => (d === 'asc' ? 'desc' : 'asc');

/** One click on a header: natural direction, then the reverse, then back to the default order. */
export function nextSort(current: QueueSort | null, column: QueueColumn): QueueSort | null {
  const first = QUEUE_COLUMNS[column].first;
  if (!current || current.column !== column) return { column, dir: first };
  if (current.dir === first) return { column, dir: opposite(first) };
  return null;
}

/** What to ask the api for. Anything it cannot order falls back to latest activity. */
export function serverSortFor(sort: QueueSort | null): ServerSort {
  if (!sort) return 'last_activity';
  const def = QUEUE_COLUMNS[sort.column];
  // The api orders the deadline column both ways (no-deadline tickets last either way).
  if (sort.column === 'deadline') return sort.dir === 'asc' ? 'due' : 'due_desc';
  return def.server && sort.dir === def.first ? def.server : 'last_activity';
}

export function parseSort(raw: string | null): QueueSort | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !(col in QUEUE_COLUMNS)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as QueueColumn, dir };
}

export function formatSort(sort: QueueSort | null): string | null {
  return sort ? `${sort.column}:${sort.dir}` : null;
}

// --- Filters in the address bar ----------------------------------------------

export const DEFAULT_FILTERS: SdQueueFilters = {
  status: OPEN_STATUSES,
  priority: [],
  assignee: '',
  category_id: null,
  store_no: '',
  sla: '',
  q: '',
  sort: 'last_activity',
};

function csv<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  if (!raw) return [];
  return raw.split(',').filter((v): v is T => (allowed as readonly string[]).includes(v));
}

/**
 * Filters and sort from the address. An absent `status` means the open
 * statuses; `status=any` means every status. Unknown values are dropped.
 */
export function readQueueParams(sp: URLSearchParams): { filters: SdQueueFilters; sort: QueueSort | null } {
  const statusRaw = sp.get('status');
  const status: SdStatus[] =
    statusRaw === null ? OPEN_STATUSES : statusRaw === 'any' ? [] : csv<SdStatus>(statusRaw, ALL_STATUSES);
  const catRaw = Number(sp.get('category'));
  const sla = sp.get('sla');
  const sort = parseSort(sp.get('sort'));
  return {
    filters: {
      status,
      priority: csv<SdPriority>(sp.get('priority'), ALL_PRIORITIES),
      assignee: sp.get('assignee') ?? '',
      category_id: Number.isInteger(catRaw) && catRaw > 0 ? catRaw : null,
      store_no: sp.get('store') ?? '',
      sla: sla === 'breached' || sla === 'warning' ? sla : '',
      q: sp.get('q') ?? '',
      sort: serverSortFor(sort),
    },
    sort,
  };
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v) => b.includes(v));

/** Writes filters and sort back, leaving out anything at its default so links stay short. */
export function writeQueueParams(filters: SdQueueFilters, sort: QueueSort | null): URLSearchParams {
  const sp = new URLSearchParams();
  if (!sameSet(filters.status, OPEN_STATUSES)) {
    sp.set('status', filters.status.length === 0 ? 'any' : filters.status.join(','));
  }
  if (filters.priority.length > 0) sp.set('priority', filters.priority.join(','));
  if (filters.assignee) sp.set('assignee', filters.assignee);
  if (filters.category_id !== null) sp.set('category', String(filters.category_id));
  if (filters.store_no) sp.set('store', filters.store_no);
  if (filters.sla) sp.set('sla', filters.sla);
  if (filters.q.trim()) sp.set('q', filters.q.trim());
  const s = formatSort(sort);
  if (s) sp.set('sort', s);
  return sp;
}

// --- Client-side ordering ------------------------------------------------------

function dueTime(row: SdStaffTicketRow): number | null {
  const dues = [row.first_response_due_at, row.resolve_due_at]
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));
  return dues.length > 0 ? Math.min(...dues) : null;
}

/** A sortable value for one cell; null means "has no value" and always sorts last. */
function cellValue(row: SdStaffTicketRow, column: QueueColumn): string | number | null {
  switch (column) {
    case 'key':
      return row.number ?? row.id;
    case 'title':
      return row.title.toLowerCase();
    case 'reporter':
      return row.reporter_name?.toLowerCase() || null;
    case 'store':
      return row.store_no ?? null;
    case 'category':
      return row.category_path.toLowerCase();
    case 'priority':
      // Higher means more urgent, so a descending sort puts critical first.
      return ALL_PRIORITIES.length - ALL_PRIORITIES.indexOf(row.priority);
    case 'status':
      return ALL_STATUSES.indexOf(row.status);
    case 'assignee':
      return row.assignee_name?.toLowerCase() || null;
    case 'deadline':
      return dueTime(row);
    case 'activity':
      return row.last_activity_at ? new Date(row.last_activity_at).getTime() : null;
  }
}

export function sortRows(rows: SdStaffTicketRow[], sort: QueueSort | null): SdStaffTicketRow[] {
  if (!sort) return rows;
  // The api already ordered these by the live next deadline (overdue first, no deadline last).
  // Re-sorting here would use the pill's clamped due dates, which cannot tell overdue tickets apart.
  if (sort.column === 'deadline') return rows;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: cellValue(row, sort.column) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      const cmp =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v), undefined, { numeric: true });
      return cmp !== 0 ? cmp * sign : a.i - b.i;
    })
    .map((x) => x.row);
}
